import React from 'react';
import { MissingAnnotationEmptyState } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { createApiRef, createPlugin, createApiFactory, discoveryApiRef, identityApiRef, configApiRef, createComponentExtension } from '@backstage/core-plugin-api';
import jsep from 'jsep';

const GRAFANA_ANNOTATION_TAG_SELECTOR = "grafana/tag-selector";
const GRAFANA_ANNOTATION_DASHBOARD_SELECTOR = "grafana/dashboard-selector";
const GRAFANA_ANNOTATION_ALERT_LABEL_SELECTOR = "grafana/alert-label-selector";
const GRAFANA_ANNOTATION_OVERVIEW_DASHBOARD = "grafana/overview-dashboard";
const isDashboardSelectorAvailable = (entity) => {
  var _a, _b;
  return ((_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[GRAFANA_ANNOTATION_DASHBOARD_SELECTOR]) || ((_b = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _b[GRAFANA_ANNOTATION_TAG_SELECTOR]);
};
const isAlertSelectorAvailable = (entity) => {
  var _a;
  return Boolean((_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[GRAFANA_ANNOTATION_ALERT_LABEL_SELECTOR]);
};
const isOverviewDashboardAvailable = (entity) => {
  var _a;
  return Boolean((_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[GRAFANA_ANNOTATION_OVERVIEW_DASHBOARD]);
};
const dashboardSelectorFromEntity = (entity) => {
  var _a, _b, _c, _d;
  return (_d = (_c = (_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[GRAFANA_ANNOTATION_DASHBOARD_SELECTOR]) != null ? _c : (_b = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _b[GRAFANA_ANNOTATION_TAG_SELECTOR]) != null ? _d : "";
};
const alertSelectorFromEntity = (entity) => {
  var _a, _b;
  return (_b = (_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[GRAFANA_ANNOTATION_ALERT_LABEL_SELECTOR]) != null ? _b : "";
};
const overviewDashboardFromEntity = (entity) => {
  var _a, _b;
  return (_b = (_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[GRAFANA_ANNOTATION_OVERVIEW_DASHBOARD]) != null ? _b : "";
};
const tagSelectorFromEntity = dashboardSelectorFromEntity;

const DashboardViewer = ({ embedUrl }) => {
  return /* @__PURE__ */ React.createElement(
    "iframe",
    {
      title: embedUrl,
      src: embedUrl,
      width: "100%",
      height: "100%",
      referrerPolicy: "strict-origin-when-cross-origin"
    }
  );
};
const EntityDashboardViewer = () => {
  const { entity } = useEntity();
  if (!isOverviewDashboardAvailable(entity)) {
    return /* @__PURE__ */ React.createElement(MissingAnnotationEmptyState, { annotation: GRAFANA_ANNOTATION_OVERVIEW_DASHBOARD });
  }
  return /* @__PURE__ */ React.createElement(DashboardViewer, { embedUrl: overviewDashboardFromEntity(entity) });
};

const includes = (haystack, needle) => {
  if (!Array.isArray(haystack)) {
    throw Error(`@> operator can only be used on an array`);
  }
  return haystack.includes(needle);
};
class QueryEvaluator {
  constructor() {
    jsep.addBinaryOp("@>", 6);
  }
  parse(query) {
    return jsep(query);
  }
  evaluate(root, context) {
    switch (root.type) {
      case "UnaryExpression":
        return this.evaluateUnaryExpression(root, context);
      case "BinaryExpression":
        return this.evaluateBinaryExpression(root, context);
      case "Identifier":
        if (!context.hasOwnProperty(root.name)) {
          throw Error(`identifier ${root.name} does not exist`);
        }
        return context[root.name];
      case "Literal":
        return root.value;
      default:
        throw Error(`unknown node type ${root.type}`);
    }
  }
  evaluateUnaryExpression(root, context) {
    switch (root.operator) {
      case "!":
        return !this.evaluate(root.argument, context);
      default:
        throw Error(`unknown unary operator ${root.operator}`);
    }
  }
  evaluateBinaryExpression(root, context) {
    switch (root.operator) {
      case "&&":
        return this.evaluate(root.left, context) && this.evaluate(root.right, context);
      case "||":
        return this.evaluate(root.left, context) || this.evaluate(root.right, context);
      case "==":
        return this.evaluate(root.left, context) === this.evaluate(root.right, context);
      case "!=":
        return this.evaluate(root.left, context) !== this.evaluate(root.right, context);
      case "@>":
        return includes(
          this.evaluate(root.left, context),
          this.evaluate(root.right, context)
        );
      default:
        throw Error(`unknown binary operator ${root.operator}`);
    }
  }
}

const grafanaApiRef = createApiRef({
  id: "plugin.grafana.service"
});
const DEFAULT_PROXY_PATH = "/grafana/api";
const isSingleWord = (input) => {
  return input.match(/^[\w-]+$/g) !== null;
};
class Client {
  constructor(opts) {
    var _a;
    this.discoveryApi = opts.discoveryApi;
    this.identityApi = opts.identityApi;
    this.proxyPath = (_a = opts.proxyPath) != null ? _a : DEFAULT_PROXY_PATH;
    this.queryEvaluator = new QueryEvaluator();
  }
  async fetch(input, init) {
    const apiUrl = await this.apiUrl();
    const authedInit = await this.addAuthHeaders(init || {});
    const resp = await fetch(`${apiUrl}${input}`, authedInit);
    if (!resp.ok) {
      throw new Error(`Request failed with ${resp.status} ${resp.statusText}`);
    }
    return await resp.json();
  }
  async listDashboards(domain, query) {
    if (isSingleWord(query)) {
      return this.dashboardsByTag(domain, query);
    }
    return this.dashboardsForQuery(domain, query);
  }
  async dashboardsForQuery(domain, query) {
    const parsedQuery = this.queryEvaluator.parse(query);
    const response = await this.fetch(`/api/search?type=dash-db`);
    const allDashboards = this.fullyQualifiedDashboardURLs(domain, response);
    return allDashboards.filter((dashboard) => {
      return this.queryEvaluator.evaluate(parsedQuery, dashboard) === true;
    });
  }
  async dashboardsByTag(domain, tag) {
    const response = await this.fetch(`/api/search?type=dash-db&tag=${tag}`);
    return this.fullyQualifiedDashboardURLs(domain, response);
  }
  fullyQualifiedDashboardURLs(domain, dashboards) {
    return dashboards.map((dashboard) => ({
      ...dashboard,
      url: domain + dashboard.url,
      folderUrl: domain + dashboard.folderUrl
    }));
  }
  async apiUrl() {
    const proxyUrl = await this.discoveryApi.getBaseUrl("proxy");
    return proxyUrl + this.proxyPath;
  }
  async addAuthHeaders(init) {
    const { token } = await this.identityApi.getCredentials();
    const headers = init.headers || {};
    return {
      ...init,
      headers: {
        ...headers,
        ...token ? { Authorization: `Bearer ${token}` } : {}
      }
    };
  }
}
class GrafanaApiClient {
  constructor(opts) {
    this.domain = opts.domain;
    this.client = new Client(opts);
  }
  async listDashboards(query) {
    return this.client.listDashboards(this.domain, query);
  }
  async alertsForSelector(dashboardTag) {
    const response = await this.client.fetch(`/api/alerts?dashboardTag=${dashboardTag}`);
    return response.map((alert) => ({
      name: alert.name,
      state: alert.state,
      url: `${this.domain}${alert.url}?panelId=${alert.panelId}&fullscreen&refresh=30s`
    }));
  }
}
class UnifiedAlertingGrafanaApiClient {
  constructor(opts) {
    this.domain = opts.domain;
    this.client = new Client(opts);
  }
  async listDashboards(query) {
    return this.client.listDashboards(this.domain, query);
  }
  async alertsForSelector(selector) {
    const response = await this.client.fetch("/api/ruler/grafana/api/v1/rules");
    const rules = Object.values(response).flat().map((ruleGroup) => ruleGroup.rules).flat();
    const [label, labelValue] = selector.split("=");
    const matchingRules = rules.filter((rule) => rule.labels && rule.labels[label] === labelValue);
    return matchingRules.map((rule) => {
      return {
        name: rule.grafana_alert.title,
        url: `${this.domain}/alerting/grafana/${rule.grafana_alert.uid}/view`,
        state: "n/a"
      };
    });
  }
}

const grafanaPlugin = createPlugin({
  id: "grafana",
  apis: [
    createApiFactory({
      api: grafanaApiRef,
      deps: { discoveryApi: discoveryApiRef, identityApi: identityApiRef, configApi: configApiRef },
      factory: ({ discoveryApi, identityApi, configApi }) => {
        const unifiedAlertingEnabled = configApi.getOptionalBoolean("grafana.unifiedAlerting") || false;
        if (!unifiedAlertingEnabled) {
          return new GrafanaApiClient({
            discoveryApi,
            identityApi,
            domain: configApi.getString("grafana.domain"),
            proxyPath: configApi.getOptionalString("grafana.proxyPath")
          });
        }
        return new UnifiedAlertingGrafanaApiClient({
          discoveryApi,
          identityApi,
          domain: configApi.getString("grafana.domain"),
          proxyPath: configApi.getOptionalString("grafana.proxyPath")
        });
      }
    })
  ]
});
const EntityGrafanaDashboardsCard = grafanaPlugin.provide(
  createComponentExtension({
    name: "EntityGrafanaDashboardsCard",
    component: {
      lazy: () => import('./index-bc38a2d2.esm.js').then((m) => m.DashboardsCard)
    }
  })
);
const EntityGrafanaAlertsCard = grafanaPlugin.provide(
  createComponentExtension({
    name: "EntityGrafanaAlertsCard",
    component: {
      lazy: () => import('./index-aacb0e82.esm.js').then((m) => m.AlertsCard)
    }
  })
);
const EntityOverviewDashboardViewer = grafanaPlugin.provide(
  createComponentExtension({
    name: "EntityOverviewDashboardViewer",
    component: {
      lazy: () => import('./index-c84b1093.esm.js').then((m) => m.EntityDashboardViewer)
    }
  })
);

export { DashboardViewer as D, EntityDashboardViewer as E, GRAFANA_ANNOTATION_DASHBOARD_SELECTOR as G, GRAFANA_ANNOTATION_TAG_SELECTOR as a, isAlertSelectorAvailable as b, GRAFANA_ANNOTATION_ALERT_LABEL_SELECTOR as c, dashboardSelectorFromEntity as d, alertSelectorFromEntity as e, grafanaPlugin as f, grafanaApiRef as g, EntityGrafanaDashboardsCard as h, isDashboardSelectorAvailable as i, EntityGrafanaAlertsCard as j, EntityOverviewDashboardViewer as k, isOverviewDashboardAvailable as l, GRAFANA_ANNOTATION_OVERVIEW_DASHBOARD as m, overviewDashboardFromEntity as o, tagSelectorFromEntity as t };
//# sourceMappingURL=index-36d5de03.esm.js.map
